import { BoolNode, SearchOperator } from 'src/services/common.types';

import { FieldOption } from '../types/search.types';

/**
 * Flat criteria rows, and the translation between them and the search DSL (JT1-1610).
 *
 * The builder used to make people construct nested AND/OR/NOT groups to say something as ordinary
 * as "not in Melbourne" — create a NOT group, then add a condition inside it. A row now says
 * "Location · is none of · Melbourne" and the negation is implied by the match type.
 *
 * All the interesting logic lives here rather than in the component, because this is the part that
 * can be wrong in ways nobody notices: a match type that compiles to the wrong operator produces a
 * search that runs, returns results, and quietly means something else.
 */

// ----------------------------------------------------------------------

/** How a row reads. Grouped by the kind of value the field holds. */
export type MatchType =
    // text
    | 'contains' | 'is_exactly' | 'does_not_contain' | 'is_not'
    // lists
    | 'is_any_of' | 'is_all_of' | 'is_none_of'
    // numbers
    | 'at_most' | 'at_least' | 'more_than' | 'less_than';

export type ValueKind = 'text' | 'list' | 'number';

export interface CriteriaRow {
    /** Local, stable, and never sent to the server. Row keys must survive deletion and reordering. */
    id: string;
    field: string;
    match: MatchType;
    value: any;
    variant?: string;
}

/** A group the flat builder cannot represent, kept so it is never silently discarded. */
export interface AdvancedRemainder {
    kind: 'advanced';
    node: BoolNode;
}

export type ParsedDsl = { rows: CriteriaRow[]; remainder: AdvancedRemainder[] };

// ----------------------------------------------------------------------

/**
 * Which family of match types a field offers.
 *
 * Derived from the field's own declared operators rather than a parallel lookup table, so the two
 * cannot drift apart when a field is added.
 */
export const valueKindOf = (field?: FieldOption): ValueKind => {
    if (!field) return 'text';
    if (field.type === 'number' || field.type === 'range') return 'number';
    if (field.type === 'multiselect' || field.type === 'select') return 'list';
    return 'text';
};

export const MATCHES_BY_KIND: Record<ValueKind, MatchType[]> = {
    text: ['contains', 'is_exactly', 'does_not_contain', 'is_not'],
    list: ['is_any_of', 'is_all_of', 'is_none_of'],
    number: ['at_most', 'at_least', 'more_than', 'less_than'],
};

export const matchesFor = (field?: FieldOption): MatchType[] => MATCHES_BY_KIND[valueKindOf(field)];

export const defaultMatchFor = (field?: FieldOption): MatchType => matchesFor(field)[0];

// ----------------------------------------------------------------------

/**
 * A match type as an operator plus whether the row is negated.
 *
 * NOTE that no negative match resolves to `nin`, and that is deliberate rather than incidental.
 * On a to-many path `$nin` inside `$some` asks "does this candidate have at least one row that is
 * not X" — which is true of almost everybody with two skills, and is not exclusion at all. Wrapping
 * a POSITIVE operator in a `NOT` node compiles through `$none`, which is the real thing. The UI
 * therefore never emits `nin`.
 */
export const resolveMatch = (
    field: FieldOption | undefined,
    match: MatchType,
): { operator: SearchOperator; negate: boolean } => {
    switch (match) {
        case 'contains':
            return { operator: 'match_phrase', negate: false };
        case 'does_not_contain':
            return { operator: 'match_phrase', negate: true };
        case 'is_exactly':
            return { operator: 'eq', negate: false };
        case 'is_not':
            return { operator: 'eq', negate: true };

        case 'is_any_of':
            return { operator: 'contains_any', negate: false };
        case 'is_all_of':
            return { operator: 'contains_all', negate: false };
        case 'is_none_of':
            // positive operator + NOT, never `nin` — see the note above
            return { operator: 'contains_any', negate: true };

        case 'at_most':
            return { operator: 'lte', negate: false };
        case 'at_least':
            return { operator: 'gte', negate: false };
        case 'more_than':
            return { operator: 'gt', negate: false };
        case 'less_than':
            return { operator: 'lt', negate: false };

        default:
            return { operator: field?.defaultOperator ?? 'eq', negate: false };
    }
};

/** The inverse, for rendering a DSL the AI produced back into rows. */
const matchFromOperator = (operator: SearchOperator, negated: boolean): MatchType => {
    switch (operator) {
        case 'match_phrase': return negated ? 'does_not_contain' : 'contains';
        case 'eq': return negated ? 'is_not' : 'is_exactly';
        case 'neq': return 'is_not';
        case 'contains_all': return negated ? 'is_none_of' : 'is_all_of';
        case 'contains_any':
        case 'in': return negated ? 'is_none_of' : 'is_any_of';
        // `nin` is never emitted by this builder, but the AI or an older saved shape can still
        // produce it, and it means the same thing a negated positive does
        case 'nin': return 'is_none_of';
        case 'lte': return 'at_most';
        case 'gte': return 'at_least';
        case 'gt': return 'more_than';
        case 'lt': return 'less_than';
        default: return 'contains';
    }
};

// ----------------------------------------------------------------------

let rowSequence = 0;
export const nextRowId = (): string => {
    rowSequence += 1;
    return `row-${rowSequence}`;
};

export const newRow = (field: FieldOption): CriteriaRow => ({
    id: nextRowId(),
    field: field.id,
    match: defaultMatchFor(field),
    value: valueKindOf(field) === 'list' ? [] : '',
});

// ----------------------------------------------------------------------

/**
 * Rows to DSL. Every row is AND-ed; a negative row becomes a `NOT` wrapping one atom.
 */
export const rowsToDsl = (rows: CriteriaRow[], fieldOptions: FieldOption[]): BoolNode => {
    const children = rows
        .filter((row) => row.field)
        .map((row) => {
            const field = fieldOptions.find((option) => option.id === row.field);
            const { operator, negate } = resolveMatch(field, row.match);

            const atom: any = { field: row.field, operator, value: row.value };
            if (row.variant) atom.variant = row.variant;

            return negate ? { op: 'NOT', children: [atom] } : atom;
        });

    return { op: 'AND', children } as BoolNode;
};

/**
 * DSL to rows.
 *
 * Handles exactly the shapes this builder produces plus the shape the AI produces: a top-level AND
 * whose children are atoms, or NOT nodes wrapping a single atom. Anything deeper is kept as a
 * remainder rather than dropped — losing a condition the user cannot see is worse than showing them
 * something they cannot edit.
 */
export const dslToRows = (dsl?: BoolNode | null): ParsedDsl => {
    const rows: CriteriaRow[] = [];
    const remainder: AdvancedRemainder[] = [];

    const asAtom = (node: any): any | null =>
        node && typeof node === 'object' && node.field ? node : null;

    const toRow = (atom: any, negated: boolean): CriteriaRow => ({
        id: nextRowId(),
        field: atom.field,
        match: matchFromOperator(atom.operator, negated),
        value: atom.value,
        variant: atom.variant,
    });

    ((dsl as any)?.children ?? []).forEach((child: any) => {
        const atom = asAtom(child);
        if (atom) {
            rows.push(toRow(atom, false));
            return;
        }

        // a NOT wrapping exactly one atom is a negative row
        const inner = child?.op === 'NOT' && child.children?.length === 1
            ? asAtom(child.children[0])
            : null;

        if (inner) {
            rows.push(toRow(inner, true));
            return;
        }

        remainder.push({ kind: 'advanced', node: child });
    });

    return { rows, remainder };
};
